import { forwardRef } from 'react';
import type { PolymorphicComponentPropWithRef, PolymorphicRef } from '../types';
import styles from './title.module.css';
import clsx from 'clsx';

export type TitleProps<As extends React.ElementType = 'h2'> =
  PolymorphicComponentPropWithRef<
    As,
    {
      uppercase?: boolean;
      text?: string;
      size?: 1 | 2 | 3 | 4 | 5 | 6 | '1' | '2' | '3' | '4' | '5' | '6' | 'body';
    }
  >;

type TitleComponent<As extends React.ElementType = 'h2'> = (
  props: TitleProps<As>
) => JSX.Element;

export type TaglineProps<As extends React.ElementType = 'span'> =
  TitleProps<As>;

const TitleComp: TitleComponent = forwardRef(function Title<
  As extends React.ElementType = 'h2'
>(
  { className, size, as, text, uppercase, ...rest }: TitleProps<As>,
  ref?: PolymorphicRef<As>
) {
  const Tag = as || 'h2';
  const sizeInt = Number(String(size || as).replace(/^h([1-6])$/i, '$1')) || 1;
  const classes = clsx(
    styles.title,
    styles[size === 'body' ? size : `size${Math.max(1, Math.min(6, sizeInt))}`],
    uppercase && styles.uppercase,
    className
  );

  // Automatically format text breaks if no children and text is provided
  // Assigning to rest.children to avoid crash with dangerouslySetInnerHTML
  if (text && !rest.children)
    Object.assign(rest, { children: formatTextBreaks(text) });

  return <Tag className={classes} ref={ref} {...rest} />;
}) as TitleComponent; // Needed to tell Typescript this does not return ReactNode but acutally JSX.Element

const Tagline = forwardRef(function Tagline<
  As extends React.ElementType = 'span'
>(props: TaglineProps<As>, ref?: PolymorphicRef<As>) {
  return (
    <Title
      as="span"
      ref={ref}
      size="6"
      uppercase
      {...props}
      className={clsx(styles.tagline, props.clasName)} // Must be after {...props}
    />
  );
}) as TitleComponent; // Needed to tell Typescript this does not return ReactNode but acutally JSX.Element

export const Title = Object.assign(TitleComp, { Tagline });

const formatTextBreaks = (text: string) =>
  text
    .replace(/--/g, '\u{AD}') // Use unicode instead of &shy; to avoid dangerouslySetInnerHTML
    .replace(/__/g, '\u{A0}') // Use unicode instead of &nbsp; to avoid dangerouslySetInnerHTML
    .replace(/(\d)\s(\d)/g, '$1\u{A0}$2'); // Format number-space-number to automatically avoid breaking up numbers i.e. 100 000
